import { useQuery } from '@tanstack/react-query';
import { getAllUsers } from '@/apis/ApiUtil';
import headerObject from '@/types/headerObj';
import useOrganizationStore from '@/state/orgState';
import useUserStore from '@/state/userState';
import User from '@/types/userType';
import { GitProvider } from '@/types/providerUser';

export interface OrganizationUserOption {
  id: string;
  name: string;
  email: string;
  gitUsername: string;
  rawUsername: string;
  platform: GitProvider;
  avatar: string | undefined;
  teamUUID: string | undefined;
}

const resolveProviderUser = (
  user: User
): { username: string; platform: GitProvider } | null => {
  if (user.GithubUsername) {
    return { username: user.GithubUsername, platform: 'github' };
  }
  if (user.GitlabUsername) {
    return { username: user.GitlabUsername, platform: 'gitlab' };
  }
  if (user.BitbucketUsername) {
    return { username: user.BitbucketUsername, platform: 'bitbucket' };
  }
  return null;
};

export const useOrganizationUsers = () => {
  const { organization } = useOrganizationStore();
  const { user } = useUserStore();

  const headerObj: headerObject = {
    Orguuid: organization?.OrgUUID || '',
    Useruuid: user?.UserUUID || '',
    Useremail: user?.Email || '',
    Username: user?.FirstName || '',
    Orgname: organization?.OrgName || '',
  };

  return useQuery({
    queryKey: ['organizationUsers', organization?.OrgUUID],
    queryFn: async () => {
      if (!organization?.OrgUUID) {
        return { Users: [] };
      }
      const response = await getAllUsers(organization.OrgUUID, headerObj);
      return response;
    },
    enabled: Boolean(organization?.OrgUUID),
    staleTime: 5 * 60 * 1000,
    select: (data): OrganizationUserOption[] => {
      return (data?.Users || [])
        .map((user: User) => {
          const providerUser = resolveProviderUser(user);
          if (!providerUser) return null;

          const first =
            user.FirstName &&
            user.FirstName !== 'null' &&
            user.FirstName !== 'undefined'
              ? user.FirstName
              : '';
          const last =
            user.LastName &&
            user.LastName !== 'null' &&
            user.LastName !== 'undefined'
              ? user.LastName
              : '';
          const fullName = `${first} ${last}`.trim();
          const nameFallback = user.Email || providerUser.username || 'User';

          return {
            id: user.UserUUID,
            name: fullName.length > 0 ? fullName : nameFallback,
            email: user.Email,
            gitUsername: `@${providerUser.username}`,
            rawUsername: providerUser.username,
            platform: providerUser.platform,
            avatar: user.Image,
            teamUUID: user.TeamUUID,
          } satisfies OrganizationUserOption;
        })
        .filter(
          (
            user: OrganizationUserOption | null
          ): user is OrganizationUserOption => user !== null
        );
    },
  });
};
